import { CSSProperties, ReactNode, cloneElement, isValidElement, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ---------- Drag handle (ASCII-styled, terminal aesthetic) ----------

export function DragHandle({
  listeners,
  attributes,
}: {
  listeners: Record<string, unknown> | undefined;
  attributes: Record<string, unknown>;
}) {
  return (
    <span
      {...(attributes as Record<string, unknown>)}
      {...(listeners as Record<string, unknown>)}
      role="button"
      aria-label="drag to reorder"
      tabIndex={0}
      style={{
        cursor: "grab",
        color: "var(--fg-dim)",
        fontFamily: "monospace",
        fontSize: "0.7rem",
        userSelect: "none",
        touchAction: "none",
        padding: "0 0.25rem",
        letterSpacing: "-0.1em",
      }}
    >
      ═══
    </span>
  );
}

type HandleProps = {
  attributes: Record<string, unknown>;
  listeners: Record<string, unknown>;
};

// ---------- Sortable container ----------

interface SortableListProps<T extends { id: string }> {
  items: T[];
  onReorder: (orderedIds: string[]) => void;
  /**
   * Render a full section (e.g. <section>) — provided as a wrapper with sort
   * ref + transform style, inside a single <div>. The consumer renders the
   * actual section element.
   */
  renderSection?: (item: T, handle: HandleProps) => ReactNode;
  /**
   * Render an inline item (e.g. <li>) — attached directly to the rendered
   * element via cloneElement, preserving valid <ul><li> structure.
   */
  renderItem?: (item: T, handle: HandleProps) => ReactNode;
}

/**
 * Exactly one of renderSection or renderItem must be provided.
 */
export function SortableList<T extends { id: string }>({
  items,
  onReorder,
  renderSection,
  renderItem,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );

  const [_activeId, setActiveId] = useState<string | null>(null);

  function handleDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id));
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(items, oldIndex, newIndex);
    onReorder(reordered.map((i) => i.id));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((i) => i.id)}
        strategy={verticalListSortingStrategy}
      >
        {renderSection
          ? items.map((item) => (
              <SectionWrapper key={item.id} id={item.id}>
                {(handleProps) => renderSection(item, handleProps)}
              </SectionWrapper>
            ))
          : renderItem
            ? items.map((item) => (
                <InlineItem key={item.id} id={item.id}>
                  {(handleProps) => renderItem(item, handleProps)}
                </InlineItem>
              ))
            : null}
      </SortableContext>
    </DndContext>
  );
}

// ---------- Wraps a full section in a <div> (allows transform-style reorder) ----------

function SectionWrapper({
  id,
  children,
}: {
  id: string;
  children: (handle: HandleProps) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} data-dragging={isDragging || undefined}>
      {children({
        attributes: attributes as unknown as Record<string, unknown>,
        listeners: listeners as unknown as Record<string, unknown>,
      })}
    </div>
  );
}

// ---------- Inline item: attaches ref + transform directly to the child element ----------

function InlineItem({
  id,
  children,
}: {
  id: string;
  children: (handle: HandleProps) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const transformStyle: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const node = children({
    attributes: attributes as unknown as Record<string, unknown>,
    listeners: listeners as unknown as Record<string, unknown>,
  });

  if (!isValidElement(node)) return node;

  const element = node as React.ReactElement<{
    ref?: React.Ref<HTMLElement>;
    style?: CSSProperties;
    "data-dragging"?: boolean;
  }>;

  const existingStyle = (element.props.style ?? {}) as CSSProperties;
  const mergedStyle: CSSProperties = { ...existingStyle, ...transformStyle };

  return cloneElement(element, {
    ref: setNodeRef,
    style: mergedStyle,
    "data-dragging": isDragging || undefined,
  });
}
